import React, { useState, useEffect } from 'react';
import { ShoppingCart, Users, DollarSign, Calendar } from 'lucide-react';
import StatsCard from './StatsCard';
import AbandonedCartsTable from '../carts/AbandonedCartsTable';
import CampaignsTable from '../campaigns/CampaignsTable';
import RecoveryChart from './RecoveryChart';
import api from '../../utils/api';
import cartresqLogo from '../../assets/cartresq-logo.jpg';
import { useLocation, useNavigate } from 'react-router-dom';
import PageLoader from '../common/PageLoader';
import { SkeletonStats, SkeletonChart, SkeletonList } from '../common/SkeletonLoaders';

const Dashboard = () => {
  const [stats, setStats] = useState({
    abandonedCarts: 0,
    recoveredCarts: 0,
    recoveryRate: 0,
    revenue: 0,
    monthlyRevenue: 0,
    activeCustomers: 0,
    trends: {
      abandonedCarts: 0,
      recoveredCarts: 0,
      recoveryRate: 0,
      revenue: 0,
      monthlyRevenue: 0,
      activeCustomers: 0
    }
  });
  
  const [chartType, setChartType] = useState('bar');
  const [timePeriod, setTimePeriod] = useState('week');
  const [chartData, setChartData] = useState([]);
  const [recentCarts, setRecentCarts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPlatform, setCurrentPlatform] = useState(null);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [subscription, setSubscription] = useState({ plan: null, status: null });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Get platform from localStorage and store connection
    const storedPlatform = localStorage.getItem('platform');
    const storeConnection = localStorage.getItem('store_connection');
    
    console.log('Dashboard platform detection:', {
      storedPlatform,
      storeConnection,
      isConnected: localStorage.getItem('isConnected')
    });
    
    if (storeConnection) {
      try {
        const connection = JSON.parse(storeConnection);
        console.log('Parsed store connection:', connection);
        setCurrentPlatform(connection.platform);
      } catch (err) {
        console.error('Error parsing store connection:', err);
        setCurrentPlatform(storedPlatform || 'woocommerce');
      }
    } else {
      setCurrentPlatform(storedPlatform || 'woocommerce');
    }
    
    // Fallback: if no platform is set but we have a store connection, use that
    if (!storedPlatform && !storeConnection) {
      const isConnected = localStorage.getItem('isConnected');
      if (isConnected === 'true') {
        console.log('No platform found but store is connected, checking for platform in store connection...');
        // Try to get platform from any available source
        const fallbackPlatform = localStorage.getItem('platform') || 'shopify';
        setCurrentPlatform(fallbackPlatform);
      }
    }
  }, []);

  useEffect(() => {
    if (!currentPlatform) {
      console.log('No platform detected, skipping dashboard data fetch');
      return;
    }

    // Only fetch dashboard data if user has subscription and store connection
    if (!subscription.status || (subscription.status !== 'active' && subscription.status !== 'trialing' && subscription.plan !== 'free')) {
      console.log('No active subscription, skipping dashboard data fetch');
      return;
    }

    const controller = new AbortController();

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching dashboard data for platform:', currentPlatform);

        // Fetch dashboard stats
        const statsResponse = await api.get('/api/dashboard/stats', {
          params: { platform: currentPlatform },
          signal: controller.signal
        });
        setStats({
          abandonedCarts: statsResponse.data.abandonedCarts || 0,
          recoveredCarts: statsResponse.data.recoveredCarts || 0,
          recoveryRate: statsResponse.data.recoveryRate || 0,
          revenue: statsResponse.data.totalRevenue || 0,
          monthlyRevenue: statsResponse.data.monthlyRevenue || 0,
          activeCustomers: statsResponse.data.activeCustomers || 0,
          trends: statsResponse.data.trends || {
            abandonedCarts: 0,
            recoveredCarts: 0,
            recoveryRate: 0,
            revenue: 0,
            monthlyRevenue: 0,
            activeCustomers: 0
          }
        });

        // Fetch recent customers
        const customersResponse = await api.get('/api/stores/customers/recent', {
          params: { platform: currentPlatform },
          signal: controller.signal
        });
        setRecentCustomers(customersResponse.data || []);

        // Fetch chart data based on time period
        const chartResponse = await api.get('/api/dashboard/chart', {
          params: { period: timePeriod, platform: currentPlatform },
          signal: controller.signal
        });
        setChartData(chartResponse.data || []);

        // Fetch recent abandoned carts
        const cartsResponse = await api.get('/api/carts/abandoned', {
          params: { limit: 4, platform: currentPlatform },
          signal: controller.signal
        });
        setRecentCarts(Array.isArray(cartsResponse.data.carts) ? cartsResponse.data.carts : Array.isArray(cartsResponse.data) ? cartsResponse.data : []);

        // Fetch campaigns - only email campaigns
        const storeId = localStorage.getItem('storeUrl') || 'default';
        const campaignsResponse = await api.get('/api/campaigns', {
          params: { limit: 10, platform: currentPlatform, storeId, type: 'email' }, // Only email campaigns
          signal: controller.signal
        });
        const campaignsData = campaignsResponse.data?.campaigns || campaignsResponse.data || [];
        // Filter to only show email campaigns (double-check on frontend)
        const emailCampaigns = Array.isArray(campaignsData) 
          ? campaignsData.filter(c => c.type === 'email')
          : [];
        setCampaigns(emailCampaigns);

      } catch (err) {
        if (err.name === 'CanceledError' || err.message === 'canceled') {
          // Ignore canceled requests
          return;
        }
        console.error('Error fetching dashboard data:', err);
        
        // Handle specific error types
        if (err.response?.status === 503) {
          const errorData = err.response.data;
          setError(errorData.message || 'Store connection failed. Please check if your store is running and accessible.');
        } else if (err.response?.status === 401) {
          const errorData = err.response.data;
          setError(errorData.message || 'Authentication failed. Please reconnect your store with valid credentials.');
        } else if (err.response?.status === 404) {
          const errorData = err.response.data;
          setError(errorData.message || 'Store not found. Please connect your store first.');
        } else {
          setError('Failed to fetch dashboard data. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();

    return () => {
      controller.abort();
    };
  }, [currentPlatform, timePeriod, subscription.status]);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        // Check if we have session_id (from Stripe redirect) or plan=free
        const urlParams = new URLSearchParams(location.search);
        const sessionId = urlParams.get('session_id');
        const planParam = urlParams.get('plan');
        
        // If we have session_id, check the session status
        if (sessionId) {
          try {
            console.log('Processing Stripe session:', sessionId);
            const sessionResponse = await api.get(`/api/subscribe/check-session?session_id=${sessionId}`);
            console.log('Session response:', sessionResponse.data);
            
            if (sessionResponse.data.success) {
              const { plan, status } = sessionResponse.data;
              console.log('✅ Session check successful, subscription:', { plan, status });
              
              // Set subscription state
              setSubscription({ plan, status });
              
              // Verify subscription was saved by checking status again
              try {
                const verifyResponse = await api.get('/api/subscribe/status');
                console.log('✅ Verified subscription status:', verifyResponse.data);
                
                if (verifyResponse.data.plan && verifyResponse.data.status) {
                  // Subscription is confirmed, update state
                  setSubscription({
                    plan: verifyResponse.data.plan,
                    status: verifyResponse.data.status
                  });
                }
              } catch (verifyErr) {
                console.error('Error verifying subscription:', verifyErr);
                // Continue anyway with the session response
              }
              
              // Check if store is connected - if not, redirect to store setup (same as free plan)
              try {
                console.log('Checking store connection...');
                const connectionRes = await api.get('/api/stores/check-connection');
                const hasConnection = connectionRes.data.hasConnection;
                console.log('Store connection status:', hasConnection);
                
                if (!hasConnection) {
                  // No store connected, redirect to store setup (like free plan)
                  console.log('No store connected, redirecting to store setup');
                  // Use window.location for more reliable redirect
                  window.location.href = '/#/store-setup';
                  return;
                }
              } catch (connectionErr) {
                // If check fails, still redirect to store setup to be safe
                console.error('Error checking store connection:', connectionErr);
                console.log('Redirecting to store setup due to connection check error');
                window.location.href = '/#/store-setup';
                return;
              }
              
              // Store is connected, continue to show dashboard
              console.log('Store is connected, showing dashboard');
              setLoading(false);
              return;
            } else {
              console.warn('❌ Session check returned success=false:', sessionResponse.data);
              // If check-session failed, try to get subscription from status endpoint
              console.log('Attempting to get subscription from status endpoint...');
            }
          } catch (sessionErr) {
            console.error('Error checking session:', sessionErr);
            console.error('Session error details:', sessionErr.response?.data);
            // Continue with normal subscription check
          }
        }
        
        // If we have plan=free, set subscription directly
        if (planParam === 'free') {
          setSubscription({ plan: 'free', status: 'active' });
          setLoading(false);
          return;
        }
        
        // Normal subscription check (no session_id)
        const response = await api.get('/api/subscribe/status');
        console.log('Subscription status:', response.data);
        
        // Ensure we have valid subscription data
        if (response.data && (response.data.plan || response.data.status)) {
          setSubscription({
            plan: response.data.plan || null,
            status: response.data.status || null
          });
          
          // If subscription is active but no store connected, redirect to store setup
          if ((response.data.status === 'active' || response.data.status === 'trialing') && response.data.plan !== 'free') {
            try {
              const connectionRes = await api.get('/api/stores/check-connection');
              const hasConnection = connectionRes.data.hasConnection;
              
              if (!hasConnection) {
                console.log('Subscription active but no store connected, redirecting to store setup');
                window.location.href = '/#/store-setup';
                return;
              }
            } catch (connectionErr) {
              console.error('Error checking store connection:', connectionErr);
              // Don't redirect on error here, let the dashboard show
            }
          }
        } else {
          // No subscription found
          console.log('No subscription found, setting to null');
          setSubscription({ plan: null, status: null });
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
        console.error('Error details:', err.response?.data || err.message);
        setSubscription({ plan: null, status: null });
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubscription();
  }, [location.search]);

  const handleViewCartDetails = (cart) => {
    // Navigate to abandoned carts page - the page will show all carts and user can find the specific one
    window.location.href = '/#/carts';
  };

  const handleEditCampaign = (campaign) => {
    window.location.href = `/campaigns/${campaign.id}`;
  };

  // Show loading while checking subscription
  if (loading) {
    return (
      <PageLoader 
        isLoading={true}
        loadingMessage="Loading your dashboard..."
        className="min-h-screen"
      />
    );
  }

  // Debug: Log current state
  console.log('Dashboard render state:', {
    loading,
    error,
    subscription,
    currentPlatform,
    stats,
    recentCarts: recentCarts.length,
    campaigns: campaigns.length
  });

  // Show subscription required message if not subscribed
  // Also check if subscription is null/undefined (not yet loaded)
  if (!subscription.status || (subscription.status !== 'active' && subscription.status !== 'trialing' && subscription.plan !== 'free')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Subscription Required</h2>
          <p className="text-gray-600 mb-6">
            You need an active subscription to access the dashboard and connect your store.
          </p>
          <a
            href="/pricing"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Choose a Plan
          </a>
        </div>
      </div>
    );
  }

  // Show store connection error message
  if (error && (error.includes('Store connection failed') || error.includes('Store not found') || error.includes('Authentication failed') || error.includes('connect your store'))) {
    return (
      <PageLoader 
        isLoading={false}
        error={error}
        errorMessage={error}
        onRetry={() => window.location.reload()}
        showRetryButton={true}
        className="min-h-screen"
      />
    );
  }

  // Fallback: If we have an error but it's not a critical store connection error, show dashboard with error message
  if (error && !error.includes('Store connection failed') && !error.includes('Store not found') && !error.includes('Authentication failed') && !error.includes('connect your store')) {
    console.warn('Dashboard has non-critical error, showing dashboard with error banner:', error);
  }

  return (
    <div className="p-6">
      {/* Error Banner */}
      {error && !error.includes('Store connection failed') && !error.includes('Store not found') && !error.includes('Authentication failed') && !error.includes('connect your store') && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Dashboard Warning
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Abandoned Carts"
          value={stats.abandonedCarts}
          icon={ShoppingCart}
          color="blue"
          trend="This Week"
          trendValue={stats.trends.abandonedCarts}
        />
        <StatsCard
          title="Recovered Carts"
          value={stats.recoveredCarts}
          icon={ShoppingCart}
          color="green"
          trend="Recovery Rate"
          trendValue={stats.trends.recoveryRate}
          showProgress
        />
        <StatsCard
          title="Recovered Revenue"
          value={`$${Number(stats.revenue).toLocaleString()}`}
          icon={DollarSign}
          color="purple"
          trend="This Week"
          trendValue={stats.trends.revenue}
        />
        <StatsCard
          title="Active Customers"
          value={stats.activeCustomers}
          icon={Users}
          color="yellow"
          trend="This Week"
          trendValue={stats.trends.activeCustomers}
        />
      </div>
      
      {/* Recovery Chart */}
      <div className="bg-white rounded-lg shadow p-4 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Cart Recovery Overview</h3>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Calendar size={18} className="text-gray-500" />
              <select
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="day">Last 24 Hours</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
            </div>
          </div>
        </div>
        <RecoveryChart 
          data={chartData} 
          chartType={chartType}
          onChartTypeChange={setChartType}
        />
      </div>
      
      {/* Recent Abandonments */}
      <AbandonedCartsTable 
        carts={recentCarts} 
        onViewDetails={handleViewCartDetails}
      />
      
      {/* Campaigns */}
      <CampaignsTable 
        campaigns={campaigns} 
        onEdit={handleEditCampaign}
        onCampaignCreated={async (updatedCampaign) => {
          if (updatedCampaign.deleted) {
            // Remove the campaign from the list
            setCampaigns(prev => prev.filter(c => c._id !== updatedCampaign._id));
          } else {
            // Refresh campaigns from server to ensure we have the latest data
            try {
              const storeId = localStorage.getItem('storeUrl') || 'default';
              const campaignsResponse = await api.get('/api/campaigns', {
                params: { limit: 10, platform: currentPlatform, storeId, type: 'email' } // Only email campaigns
              });
              const campaignsData = campaignsResponse.data?.campaigns || campaignsResponse.data || [];
              // Filter to only show email campaigns (double-check on frontend)
              const emailCampaigns = Array.isArray(campaignsData) 
                ? campaignsData.filter(c => c.type === 'email')
                : [];
              setCampaigns(emailCampaigns);
            } catch (err) {
              console.error('Error refreshing campaigns after creation:', err);
              // Fallback: try to add the new campaign to the existing list
              if (updatedCampaign._id) {
                setCampaigns(prev => {
                  const existingIndex = prev.findIndex(c => c._id === updatedCampaign._id);
                  if (existingIndex >= 0) {
                    // Update existing campaign
                    const newList = [...prev];
                    newList[existingIndex] = updatedCampaign;
                    return newList;
                  } else {
                    // Add new campaign
                    return [updatedCampaign, ...prev];
                  }
                });
              }
            }
          }
        }}
      />
    </div>
  );
};

export default Dashboard; 